import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  loadAdminStory, saveAdminStoryEdit, publishAdminStory,
  rejectAdminStory, markNeedsRedaction, requestClarification,
  saveAdminStoryFacets, listCategories,
} from "@/lib/admin.client";
import type { AdminDict, AdminLang } from "@/lib/admin-i18n";
import { Checklist, EMPTY_CHECKLIST, isChecklistComplete, type ChecklistState } from "./Checklist";
import { TranslationsPanel } from "./TranslationsPanel";

export function StoryDetail({
  t, lang, id, onBack,
}: {
  t: AdminDict;
  lang: AdminLang;
  id: string;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const fetchStory = loadAdminStory;
  const saveEdit = saveAdminStoryEdit;
  const publish = publishAdminStory;
  const reject = rejectAdminStory;
  const markNR = markNeedsRedaction;
  const clarify = requestClarification;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "story", id],
    queryFn: () => fetchStory({ data: { id } }),
  });

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [reason, setReason] = useState("");
  const [checklist, setChecklist] = useState<ChecklistState>(EMPTY_CHECKLIST);
  const [notice, setNotice] = useState<string | null>(null);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [warningsText, setWarningsText] = useState("");
  const [facetsNotice, setFacetsNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setTitle(data.story.title ?? "");
    setBody(data.story.body ?? "");
    setReason("");
    setChecklist(EMPTY_CHECKLIST);
    setCategoryIds(Array.isArray(data.story.category_ids) ? (data.story.category_ids as string[]) : []);
    setWarningsText(
      Array.isArray(data.story.content_warnings)
        ? (data.story.content_warnings as string[]).join(", ")
        : "",
    );
    setFacetsNotice(null);
  }, [data?.story.id]);

  const cats = useQuery({
    queryKey: ["admin", "categories"],
    queryFn: () => listCategories(),
    staleTime: 5 * 60 * 1000,
  });

  const isConfessor = data?.story.submission_type === "confessor";
  const complete = useMemo(() => isChecklistComplete(checklist) || (!isConfessor && (() => {
    const c = { ...checklist, confessor_anonymity: true };
    return isChecklistComplete(c);
  })()), [checklist, isConfessor]);

  const save = useMutation({
    mutationFn: () => saveEdit({ data: { id, title, body, reason } }),
    onSuccess: () => {
      setNotice(t.saved);
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin", "story", id] });
    },
    onError: (e) => setNotice((e as Error).message),
  });

  const doPublish = useMutation({
    mutationFn: () => {
      const payload = isConfessor ? checklist : { ...checklist, confessor_anonymity: true as const };
      return publish({ data: { id, checklist: payload as any } });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin"] });
      onBack();
    },
    onError: (e) => setNotice((e as Error).message),
  });

  const doReject = useMutation({
    mutationFn: (r: string) => reject({ data: { id, reason: r } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin"] }); onBack(); },
    onError: (e) => setNotice((e as Error).message),
  });

  const doNR = useMutation({
    mutationFn: (r: string) => markNR({ data: { id, reason: r } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin"] }); refetch(); },
    onError: (e) => setNotice((e as Error).message),
  });

  const doClarify = useMutation({
    mutationFn: (note: string) => clarify({ data: { id, note } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin"] }); refetch(); },
    onError: (e) => setNotice((e as Error).message),
  });

  const saveFacets = useMutation({
    mutationFn: () => {
      const warnings = warningsText
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      return saveAdminStoryFacets({ data: { id, category_ids: categoryIds, content_warnings: warnings } });
    },
    onSuccess: () => {
      setFacetsNotice(t.facets_saved);
      qc.invalidateQueries({ queryKey: ["admin", "story", id] });
    },
    onError: (e) => setFacetsNotice((e as Error).message),
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">…</p>;
  if (error) return <p className="text-sm" style={{ color: "var(--rust)" }}>{(error as Error).message}</p>;
  if (!data) return null;

  const { story, raw } = data;
  const log = Array.isArray(story.redaction_log) ? (story.redaction_log as any[]) : [];

  const nameFor = (row: {
    slug: string;
    name_en: string | null;
    name_ru: string | null;
    name_fr: string | null;
    name_es: string | null;
    name_zh: string | null;
    name_ar: string | null;
  }): string => {
    const byLang: Record<AdminLang, string | null> = {
      en: row.name_en,
      ru: row.name_ru,
      fr: row.name_fr,
      es: row.name_es,
      zh: row.name_zh,
      ar: row.name_ar,
    };
    return byLang[lang] ?? row.name_en ?? row.slug;
  };

  const toggleCategory = (cid: string) => {
    setCategoryIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid],
    );
  };

  return (
    <section className="space-y-6">
      <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground">
        {t.back_to_queue}
      </button>

      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {story.language.toUpperCase()} · {isConfessor ? t.type_confessor : t.type_victim} · {story.status}
          </p>
          <h2 className="font-display text-2xl" style={{ color: "var(--ink)" }}>
            {story.title ?? <em className="text-muted-foreground">untitled</em>}
          </h2>
        </div>
        <span className="text-xs" style={{ color: story.has_contact ? "var(--ink)" : "var(--fog)" }}>
          {story.has_contact ? t.contact_yes : t.contact_no}
        </span>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Original (read-only) */}
        <div>
          <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t.original_header}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {t.original_lang}: {(raw?.raw_language ?? story.language).toUpperCase()}
          </p>
          <pre
            className="mt-2 max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-sm border p-3 font-mono text-[13px] leading-relaxed"
            style={{ borderColor: "var(--fog)", background: "color-mix(in oklab, var(--paper) 96%, var(--ink) 4%)" }}
          >
            {raw?.raw_body ?? "(no raw copy stored)"}
          </pre>
        </div>

        {/* Editable */}
        <div>
          <h3 className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t.edited_header}
          </h3>
          <label className="mt-2 block text-xs text-muted-foreground">{t.title_label}</label>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
          />
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)}
            rows={18}
            className="mt-3 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm leading-relaxed"
          />
          <label className="mt-3 block text-xs text-muted-foreground">{t.reason_label}</label>
          <input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={t.reason_placeholder}
            className="mt-1 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            disabled={save.isPending || reason.trim().length < 3}
            onClick={() => save.mutate()}
            className="mt-3 rounded-full border px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
          >
            {save.isPending ? "…" : t.save_edit}
          </button>
          {notice && <span className="ml-3 text-xs text-muted-foreground">{notice}</span>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        {/* Actions + meta */}
        <div className="space-y-4">
          <div className="rounded-sm border border-border p-4">
            <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.facets}</h4>
            <p className="mt-1 text-xs text-muted-foreground">{t.facets_edit_hint}</p>

            <div className="mt-3">
              <label className="text-xs text-muted-foreground">{t.categories_label}</label>
              {cats.isLoading && (
                <p className="mt-1 text-xs text-muted-foreground">{t.categories_loading}</p>
              )}
              {cats.error && (
                <p className="mt-1 text-xs" style={{ color: "var(--rust)" }}>
                  {(cats.error as Error).message}
                </p>
              )}
              {cats.data && (
                <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {cats.data.map((c) => {
                    const checked = categoryIds.includes(c.id);
                    return (
                      <li key={c.id}>
                        <label className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCategory(c.id)}
                            className="mt-1"
                          />
                          <span>{nameFor(c)}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-4">
              <label className="text-xs text-muted-foreground">{t.warnings_label}</label>
              <input
                value={warningsText}
                onChange={(e) => setWarningsText(e.target.value)}
                placeholder={t.content_warnings_placeholder}
                className="mt-1 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">{t.content_warnings_hint}</p>
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                disabled={saveFacets.isPending}
                onClick={() => saveFacets.mutate()}
                className="rounded-full border px-4 py-1.5 text-sm font-medium disabled:opacity-50"
                style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
              >
                {saveFacets.isPending ? "…" : t.save_facets}
              </button>
              {facetsNotice && (
                <span className="text-xs text-muted-foreground">{facetsNotice}</span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              disabled={!complete || doPublish.isPending}
              onClick={() => doPublish.mutate()}
              className="rounded-full px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: "var(--amber)", color: "var(--ink)" }}
              title={!complete ? t.publish_disabled_hint : undefined}
            >
              {t.publish}
            </button>
            <button
              onClick={() => {
                const r = window.prompt(t.needs_redaction_prompt);
                if (r && r.trim().length >= 3) doNR.mutate(r.trim());
              }}
              className="rounded-full border px-4 py-2 text-sm font-medium"
              style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
            >
              {t.needs_redaction}
            </button>
            <button
              disabled={!story.has_contact}
              title={!story.has_contact ? t.no_contact_disabled : undefined}
              onClick={() => {
                const n = window.prompt(t.clarify_prompt);
                if (n && n.trim().length >= 3) doClarify.mutate(n.trim());
              }}
              className="rounded-full border px-4 py-2 text-sm font-medium disabled:opacity-50"
              style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
            >
              {t.clarify}
            </button>
            <button
              onClick={() => {
                const r = window.prompt(t.reject_prompt);
                if (r && r.trim().length >= 3) doReject.mutate(r.trim());
              }}
              className="ml-auto rounded-full border px-4 py-2 text-sm font-medium"
              style={{ borderColor: "var(--rust)", color: "var(--rust)" }}
            >
              {t.reject}
            </button>
          </div>
          {!complete && (
            <p className="text-xs text-muted-foreground">{t.publish_disabled_hint}</p>
          )}

          <div className="rounded-sm border border-border p-4">
            <h4 className="text-[11px] uppercase tracking-wider text-muted-foreground">{t.redaction_log}</h4>
            {log.length === 0 ? (
              <p className="mt-2 text-sm italic text-muted-foreground">{t.redaction_empty}</p>
            ) : (
              <ul className="mt-2 space-y-2 text-xs">
                {log.slice().reverse().map((e, i) => (
                  <li key={i} className="border-l-2 pl-2" style={{ borderColor: "var(--fog)" }}>
                    <span className="tabular-nums text-muted-foreground">
                      {String(e.at ?? "").slice(0, 19).replace("T", " ")}
                    </span>{" "}
                    <span className="font-medium">{String(e.what ?? "edit")}</span>
                    {e.reason && <> — <span>{String(e.reason)}</span></>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div
            className="rounded-sm border border-dashed p-4 text-xs"
            style={{ borderColor: "var(--fog)", color: "var(--fog)" }}
          >
            <p className="font-medium" style={{ color: "var(--ink)" }}>{t.triage_title}</p>
            <p className="mt-1">{t.triage_body}</p>
          </div>

          <p className="text-[11px] text-muted-foreground">{t.privacy_note}</p>
        </div>

        <Checklist t={t} value={checklist} onChange={setChecklist} isConfessor={isConfessor} />
      </div>

      {(story as any).canonical_id == null && (
        <TranslationsPanel
          t={t}
          canonicalId={story.id}
          original={{
            title: story.title ?? null,
            body: story.body ?? null,
            language: story.language,
          }}
        />
      )}
    </section>
  );
}